import React, { useState, useEffect } from "react";
import styles from "./styles.module.scss";
import { cls } from "app/utils";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Offering from "./Offering";
import { Loading } from "app/components/Wrappers";
import { RootState } from "typedefs";
import { useSelector } from "react-redux";
import { schedulingFetchGet } from "app/utils/request/scheduling/fetch";
import ButtonVariant from "app/components/Button/ButtonVariant";
import OfferingsFilters from "./OfferingsFilters";
import PaymentGuardModal, {
  PaymentGuardChoice,
} from "../NewOffering/PaymentGuardModal";
import usePaymentGuard from "app/utils/hooks/usePaymentGuard";

interface PropsDef {
  enabledIntegration?: boolean;
  apiBasePath?: string;
  facilityId?: string;
  isLoadingData?: boolean;
}

export default function Offerings(props: PropsDef) {
  const {
    enabledIntegration = false,
    apiBasePath = "/stripe_products",
    facilityId,
    isLoadingData = false,
  } = props;
  const  { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [activeProducts, setActiveProducts] = useState([]);
  const [inactiveProducts, setInactiveProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const userId = useSelector((state: RootState) => state.user.self.id);
  const [refresh, setRefresh] = useState(true);
  const refreshHandler = () => {
    setRefresh((prev) => !prev);
  };

  // Clone opens a creation form, so it goes through the payment guard like
  // the offering chooser does (spec §5: every creation while Stripe is
  // unconnected). The list owns one modal for all its cards; the product
  // awaiting the answer is held here.
  const guard = usePaymentGuard(facilityId ?? null);
  const [cloning, setCloning] = useState<any>(null);
  const openCloneForm = (product, collectType?: PaymentGuardChoice) => {
    history.push({
      pathname:
        product?.productType === "product"
          ? "/payment/create"
          : "/subscription/create",
      search: facilityId ? `?facilityId=${facilityId}` : "",
      // The guard's choice overrides the cloned product's payment method.
      state: collectType ? { product, collectType } : { product },
    });
  };
  const requestClone = (product) => {
    // An unknown Stripe status opens the guard too (never skip it on the
    // absence of evidence); the modal follows the status once it lands and
    // holds Confirm until then.
    if (guard.academyCoach && (!guard.stripeStatusKnown || guard.required)) {
      setCloning(product);
      return;
    }
    openCloneForm(product);
  };

  useEffect(() => {
    const abortController = new AbortController();

    setLoading(true);
    setActiveProducts([]);
    setInactiveProducts([]);

    // Build query params from URL
    const params = new URLSearchParams(location.search);
    const queryParams: string[] = [];

    // Only add coach_id param for coach-level usage (when no facilityId)
    if (!facilityId) {
      queryParams.push(`coach_id=${userId}`);
    }

    const title = params.get('title');
    if (title) {
      queryParams.push(`title=${encodeURIComponent(title)}`);
    }

    const status = params.get('status');
    if (status) {
      queryParams.push(`status=${encodeURIComponent(status)}`);
    }

    const collect_type = params.get('collect_type');
    if (collect_type) {
      queryParams.push(`collect_type=${encodeURIComponent(collect_type)}`);
    }

    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';

    schedulingFetchGet(`${apiBasePath}${queryString}`, {}, {}, abortController.signal)
      .then((data) => {
        if (!data) {
          throw new Error('Unexpected response format');
        }
        setActiveProducts(data.filter((product) => product.enabled === true));
        setInactiveProducts(data.filter((product) => product.enabled === false));
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          return;
        }
        console.error('Failed to fetch products:', error);
      })
      .finally(() => {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      });
    return () => {
      abortController.abort();
    };
  }, [refresh, userId, location.search, apiBasePath, facilityId]);

  // Helper function to check if filters are active
  const hasActiveFilters = () => {
    const params = new URLSearchParams(location.search);
    return params.has('title') || params.has('status') || params.has('collect_type');
  };

  // Render functions for different states
  const renderLoading = () => (
    <Loading isLoading={true} loadType="spinner" />
  );

  const renderNoOfferings = () => (
    <div className={styles.info_container}>
      <div className={styles.gray_text}>
        {t("You can create packages and subscriptions that can be purchased by your athletes.")}
      </div>
      <div className={styles.centered_text}>
        {t("You have not created any offerings.")}
        <br/>
        {t("All of your packages and subscriptions will be displayed here.")}
      </div>
    </div>
  );

  const renderNoResults = () => (
    <div className={styles.info_container}>
      <div className={styles.centered_text}>
        {t("No offerings match your filters.")}
      </div>
    </div>
  );

  const renderActiveProducts = () => {
    if (activeProducts.length === 0) {
      return null;
    }

    return (
      <div className={styles.offerings_container}>
        {activeProducts.map((product) => (
          <Offering
            key={product.id}
            product={product}
            dataRefresher={refreshHandler}
            enabledIntegration={enabledIntegration}
            facilityId={facilityId}
            onClone={requestClone}
          />
        ))}
      </div>
    );
  };

  const renderInactiveProducts = () => {
    if (inactiveProducts.length === 0) {
      return null;
    }

    return (
      <>
        <div className={styles.subtitle}>{t('Inactive')}</div>
        <div className={styles.offerings_container}>
          {inactiveProducts.map((product) => (
            <Offering
              key={product.id}
              product={product}
              dataRefresher={refreshHandler}
              enabledIntegration={enabledIntegration}
              facilityId={facilityId}
              onClone={requestClone}
            />
          ))}
        </div>
      </>
    );
  };

  const renderProducts = () => (
    <div className={styles.offerings_section}>
      {renderActiveProducts()}
      {renderInactiveProducts()}
    </div>
  );

  const renderContent = () => {
    if (isLoadingData || loading) {
      return renderLoading();
    }

    const hasProducts = activeProducts.length > 0 || inactiveProducts.length > 0;

    if (!hasProducts) {
      return hasActiveFilters() ? renderNoResults() : renderNoOfferings();
    }

    return renderProducts();
  };

  return (
  <div className={cls(styles.container)}>
    <div className={styles.header_container}>
        <div className={styles.header_title_container}>
          <div className={styles.header_title_wrapper}>
          <h2 className={cls(styles.title)}>
            {t('Offerings')}
            </h2>
          </div>
        </div>
        <ButtonVariant
          title={`+ ${t('Create New')}`}
          buttonType='primary-outline'
          onClickFunc={() => history.push(facilityId ? `/offerings/create?facilityId=${facilityId}` : '/offerings/create')}
          buttonStyles={styles.create_new_button}
        />
    </div>
    {!loading && (activeProducts.length > 0 || inactiveProducts.length > 0 || hasActiveFilters()) && (
      <OfferingsFilters />
    )}
    {renderContent()}
    {cloning && (
      <PaymentGuardModal
        isOpen
        objectType={
          cloning.productType === "product" ? "package" : "subscription"
        }
        stripeConnected={guard.stripeConnected}
        // Opened before the status has settled (see requestClone), so
        // Confirm is held until it has: the modal follows the status once it
        // lands, and a choice made on a stale one would be permanent.
        stripeStatusKnown={guard.stripeStatusKnown}
        // Cancel, the close icon, Escape and the overlay only close the
        // guard: nothing is cloned or marked. The form opens only through
        // Confirm with a selected option.
        onClose={() => setCloning(null)}
        onConfirm={(choice) => {
          setCloning(null);
          guard.confirm(choice);
          openCloneForm(cloning, choice);
        }}
      />
    )}
  </div>
);}

